import React from 'react';
import PropTypes from 'prop-types';
import Markdown from 'markdown-to-jsx';
import CodeArtifactCard from '../tds2_shim/agentforce/CodeArtifactCard';
import MarkdownLinkCta from './MarkdownLinkCta';

// Defense-in-depth: bypass markdown parsing for pathologically large content
// and render as plain text. Chat content is length-bounded server-side, so this
// never trips on real output.
const INLINE_MARKDOWN_MAX_LINE = 10000;

// A top-level list item whose entire content is a bold label (optionally
// ending in a colon) — e.g. "- **Subagents:**". The LLM emits a section label
// as its own bullet, which renders as a stray bullet above the real entries.
// We promote these to an h3 heading so the entries read as bullets underneath
// (T1-4). Anchored at line start; the bold run excludes `*` so it can't span
// past the closing `**`, which bounds backtracking (no catastrophic regex).
const BOLD_LABEL_LIST_ITEM = /^[ \t]*[-*][ \t]+\*\*([^*\n]+?):?\*\*[ \t]*$/gm;

// `[ \t]+` is intentional in place of `\s+`: keeping the whitespace class
// off newlines bounds backtracking to a single line's run of spaces/tabs and
// satisfies SAST regex-DoS checks. The intent is "split an inline-glued list
// marker onto its own line" — only intra-line whitespace matters.
function preprocess(s) {
  if (!s) return '';
  return s
    .replace(/[ \t]+(\d+\.[ \t]+)/g, '\n$1')
    .replace(/[ \t]+([-*][ \t]+)/g, '\n$1')
    .replace(/([^\n])\n(\d+\.[ \t]+|[-*][ \t]+)/g, '$1\n\n$2')
    // Promote bold-label-only list items to headings. Run AFTER the splitters
    // above so an inline-glued label is already on its own line. The trailing
    // blank line keeps the heading a separate block from the list that follows.
    .replace(BOLD_LABEL_LIST_ITEM, '\n### $1\n')
    .trim();
}

export function flattenChildrenToText(children) {
  let out = '';
  React.Children.forEach(children, (child) => {
    if (child === null || child === undefined || child === false) return;
    if (typeof child === 'string' || typeof child === 'number') {
      out += String(child);
      return;
    }
    if (React.isValidElement(child)) {
      out += flattenChildrenToText(child.props.children);
    }
  });
  return out;
}

// Returns {href, label} when `children` is exactly one anchor element
// (with text content) and nothing else — used to promote a paragraph
// containing only a link into a CTA card.
function asStandaloneLink(children) {
  const arr = React.Children.toArray(children).filter((child) => {
    if (typeof child === 'string') return child.trim().length > 0;
    return true;
  });
  if (arr.length !== 1) return null;
  const only = arr[0];
  if (!React.isValidElement(only)) return null;
  // The `a` override below renders a `MarkdownAnchor` (function component), not
  // a raw <a>. Match either the raw tag or the override.
  if (only.type !== 'a' && only.type !== MarkdownAnchor) return null;
  const rawHref = only.props && only.props.href;
  const href = safeHref(rawHref);
  if (!href) return null;
  const label = flattenChildrenToText(only.props.children).trim();
  if (!label) return null;
  return { href, label };
}

export function describeCodeBlock(body, language) {
  const looksLikeAgentScript =
    /developer_name|system_instructions|subagents|start_agent|agent_name|agent_label/i.test(body);
  if (looksLikeAgentScript) {
    return { title: 'AgentScript Draft', language: 'agentscript' };
  }
  if (language === 'yaml' || language === 'agentscript') {
    return { title: 'AgentScript', language };
  }
  return { title: 'Code', language: language || 'text' };
}

function maybePrettyPrint(body, language) {
  const trimmed = (body || '').trim();
  const isJsonLang = language ? /^(json|javascript|js|ts|typescript)$/i.test(language) : false;
  const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('[');
  if (isJsonLang || looksLikeJson) {
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch (e) {
      return body;
    }
  }
  return body;
}

// Only http(s) URLs and same-origin/relative paths are allowed.
// Returns the original href if safe, or null.
export function safeHref(href) {
  if (!href || typeof href !== 'string') return null;
  const trimmed = href.trim();
  if (!trimmed) return null;
  if (/^(https?:|mailto:)/i.test(trimmed)) return trimmed;
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null;
  return trimmed;
}

function MarkdownAnchor({ href, children, ...rest }) {
  const safe = safeHref(href);
  if (!safe) {
    return <span className="tds2-agentforce-modal__markdown-link">{children}</span>;
  }
  return (
    <a
      {...rest}
      href={safe}
      target="_blank"
      rel="noopener noreferrer"
      className="tds2-agentforce-modal__markdown-link"
    >
      {children}
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="tds2-agentforce-modal__markdown-link-external"
      >
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </svg>
    </a>
  );
}

MarkdownAnchor.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node,
};

function MarkdownParagraph({ children }) {
  const cta = asStandaloneLink(children);
  if (cta) {
    return <MarkdownLinkCta href={cta.href} label={cta.label} />;
  }
  return <p>{children}</p>;
}

MarkdownParagraph.propTypes = {
  children: PropTypes.node,
};

// markdown-to-jsx renders fenced code as <pre><code className="lang-foo">…</code></pre>.
// We override `pre`: when its single child is a <code> element with a body, route it
// through CodeArtifactCard. Otherwise render a plain <pre>.
export function MarkdownPre({ children }) {
  const arr = React.Children.toArray(children);
  const codeEl = arr.find(
    (child) => React.isValidElement(child) && (child.type === 'code' || child.type === MarkdownInlineCode),
  );
  if (codeEl) {
    const className = (codeEl.props && codeEl.props.className) || '';
    const langMatch = className.match(/(?:lang|language)-([\w-]+)/);
    const language = langMatch ? langMatch[1] : '';
    const rawBody = flattenChildrenToText(codeEl.props.children).replace(/\n$/, '');
    const meta = describeCodeBlock(rawBody, language);
    const body = maybePrettyPrint(rawBody, meta.language);
    return <CodeArtifactCard title={meta.title} language={meta.language} body={body} />;
  }
  return <pre>{children}</pre>;
}

MarkdownPre.propTypes = {
  children: PropTypes.node,
};

export function MarkdownInlineCode({ children, className }) {
  // Fenced code blocks pass through here too, but they're already wrapped in
  // <pre> by markdown-to-jsx and the `pre` override owns the rendering. We
  // defer to the default <code> output in that case via a className signal.
  if (className && /(?:lang|language)-/.test(className)) {
    return <code className={className}>{children}</code>;
  }
  return <code className="tds2-agentforce-modal__inline-code">{children}</code>;
}

MarkdownInlineCode.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

const MARKDOWN_OPTIONS = {
  forceBlock: true,
  overrides: {
    a: { component: MarkdownAnchor },
    p: { component: MarkdownParagraph },
    pre: { component: MarkdownPre },
    code: { component: MarkdownInlineCode },
  },
};

export default function ChatMarkdown({ content }) {
  if (!content) return null;
  if (content.length > INLINE_MARKDOWN_MAX_LINE) {
    return (
      <div className="tds2-agentforce-modal__markdown">
        <span>{content}</span>
      </div>
    );
  }
  const normalized = preprocess(content);
  if (!normalized) return null;
  return (
    <div className="tds2-agentforce-modal__markdown">
      <Markdown options={MARKDOWN_OPTIONS}>{normalized}</Markdown>
    </div>
  );
}

ChatMarkdown.propTypes = {
  content: PropTypes.string,
};
